import React from 'react';
import { Button } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { CheckCircle } from '@material-ui/icons';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useParams } from 'react-router-dom';
import { eventNames } from '../../lib/constants';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import { getTimezoneCode, concatStrings } from '../../lib/utils';
import * as auditHistoryActions from '../../store/envelopeHistory/actions';
import { sendEmail } from '../../store/envelopeHistory/actions';
import {
  type IAuditHistoryBody,
  type IEnvelopeHistoryBody,
  type IGroupDetails,
} from '../../store/envelopeHistory/types';
import { hideAlert } from '../../store/pageFrame/actions';
import Table from '../common/Table';
import {
  type AuditEventWithRecipientInfo,
  generateEventContent,
  getEventsWithRecipientInfo,
} from './activity-log-helpers';

dayjs.extend(advancedFormat);

export function ActivityLog() {
  const dispatch = useDispatch();
  const params = useParams<{ id: string }>();

  const { envelope, auditHistory, recipientsWhoHaveNotViewed } = useShallowSelector((state) => ({
    isFetching: state.envelopeHistory.isFetching,
    auditHistory: state.envelopeHistory.auditHistory,
    envelope: state.envelopeHistory.envelope,
    recipientsWhoHaveNotViewed: state.envelopeHistory.recipientsWhoHaveNotViewed,
  }));

  const { senderFirstName, senderMiddleName, senderLastName } = envelope;
  const senderName = concatStrings(senderFirstName, senderMiddleName, senderLastName);
  const recipientInfo = envelope.recipients.reduce((acc, r) => ({ ...acc, [r.id]: r }), {});

  function sendReminder(recipientId: string) {
    dispatch(hideAlert());
    dispatch(
      sendEmail(envelope.id, {
        recipient: { recipientId },
        emailType: 'ReviewAndSign',
        status: 'Sent',
        isResend: 'true',
      })
    );
    dispatch(auditHistoryActions.fetch(params.id));
  }

  return (
    <div className="pt-16 pb-8">
      <Typography variant="overline">Activity Log</Typography>
      <Paper className="mt-8" elevation={0}>
        <Table
          activityLog
          rows={getEventsWithRecipientInfo(auditHistory, recipientInfo)}
          columns={[
            {
              key: 'eventName',
              label: 'Activity',
              cell: (event: AuditEventWithRecipientInfo) => (
                <Typography className="max-w-xl break-words" data-spec={event.eventName}>
                  {generateEventContent({
                    event,
                    senderId: envelope.senderId,
                    senderName,
                    recipientsWhoHaveNotViewed,
                  })}
                </Typography>
              ),
            },
            {
              key: 'date',
              label: 'Date',
              align: 'left' as const,
              cell: (e: AuditEventWithRecipientInfo) => (
                <Typography className="whitespace-nowrap">
                  {e.eventName !== eventNames.WaitingFor ? dayjs(e.date).format('MMMM Do, YYYY') : ''}
                </Typography>
              ),
            },
            {
              key: 'time',
              label: 'Time',
              align: 'left' as const,
              cell: (event: AuditEventWithRecipientInfo) => (
                <Typography className="whitespace-nowrap">
                  {event.eventName !== eventNames.WaitingFor
                    ? dayjs(event.time).format('hh:mm A').concat(' ', getTimezoneCode())
                    : null}
                </Typography>
              ),
            },
            {
              key: 'action',
              label: 'Action',
              align: 'left' as const,
              cell: (event: AuditEventWithRecipientInfo) => (
                <ActivityLogResendButton envelope={envelope} event={event} onClick={sendReminder} />
              ),
            },
          ]}
        />
      </Paper>
    </div>
  );
}

function ActivityLogResendButton({
  envelope,
  event,
  onClick,
}: {
  envelope: IEnvelopeHistoryBody;
  event: IAuditHistoryBody;
  onClick: (arg: string) => void;
}) {
  const [isResendClicked, setIsResendClicked] = React.useState(false);

  function handleClick(recipientId: string) {
    setIsResendClicked(true);
    onClick(recipientId);
  }

  const group = envelope.signerDetails?.details?.find(
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    (d: IGroupDetails) => d.value.signerId === event.eventData.recipientId
  );
  const isInActiveSignerGroup = group?.value.signingGroupId === envelope?.signerDetails?.activeGroup;

  return (
    <>
      {isInActiveSignerGroup && event.eventName === eventNames.WaitingFor && (
        <Button
          data-spec="resend-button"
          variant={isResendClicked ? 'text' : 'outlined'}
          color="primary"
          disabled={isResendClicked}
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          onClick={() => handleClick(event.eventData.recipientId)}
          startIcon={isResendClicked ? <CheckCircle /> : null}
        >
          {isResendClicked ? 'Reminder Sent' : 'Remind & Resend'}
        </Button>
      )}
    </>
  );
}
